import React, { useCallback, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import DownloadIcon from '@mui/icons-material/Download';
import LoadingButton from '@mui/lab/LoadingButton';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import { GridColDef } from '@mui/x-data-grid-premium';
import { reportService } from '../../api';
import { Table } from '../../components/Table';
import { fetchStatuses } from '../../constants/fetchStatuses';
import { fetchOrdersReport } from '../../redux/modules/report/report.actions';
import { reportSelector } from '../../redux/modules/report/report.selectors';
import type { OrdersReportApiItem, ReportApiDateRangeQuery } from '../../types/report';
import { gridFormatDecimalString } from './reportGridFormatters';
import { ReportDateRangeFilter } from './ReportDateRangeFilter';
import { useReportCsvExport } from './useReportCsvExport';

function ordersReportRowId(row: OrdersReportApiItem) {
  return `${row.order_id}-${row.index_fund_id}`;
}

export function OrdersReportPage() {
  const [dateRange, setDateRange] = useState<ReportApiDateRangeQuery | null>(null);
  const onDateRangeChange = useCallback((r: ReportApiDateRangeQuery | null) => {
    setDateRange(r);
  }, []);

  const columns = useRef<GridColDef[]>([
    { field: 'user_id', headerName: 'User Id', type: 'number', width: 100 },
    { field: 'order_id', headerName: 'Order Id', minWidth: 280, flex: 0.4 },
    { field: 'order_created_date', headerName: 'Created', type: 'dateTime', width: 180 },
    { field: 'order_updated_date', headerName: 'Updated', type: 'dateTime', width: 180 },
    { field: 'order_approved_date', headerName: 'Approved', type: 'dateTime', width: 180 },
    { field: 'status', headerName: 'Status', width: 120 },
    { field: 'category', headerName: 'Category', width: 100 },
    { field: 'index_fund_id', headerName: 'Fund Id', type: 'number', width: 100 },
    { field: 'index_fund_name', headerName: 'Fund name', minWidth: 200, flex: 0.5 },
    { field: 'asset_class', headerName: 'Asset class', minWidth: 160, flex: 0.3 },
    { field: 'type', headerName: 'Type', width: 130 },
    { field: 'actual_units', headerName: 'Actual units', width: 160, valueFormatter: gridFormatDecimalString },
    { field: 'actual_amount', headerName: 'Actual amount', width: 150, valueFormatter: gridFormatDecimalString },
    { field: 'is_premium_fund', headerName: 'Premium fund', width: 130, sortable: false, filterable: false },
  ]);

  const { ordersReportItems, totalOrdersReportItems, fetchOrdersReportStatus, error } = useSelector(reportSelector);

  const { exportCsv, loading: exportCsvLoading } = useReportCsvExport(
    'orders-report.csv',
    useCallback(() => reportService.exportOrdersReportCsv(), []),
  );

  return (
    <>
      {error && <Alert severity="error">{error.message}</Alert>}
      <Grid container flexDirection="column" component={Paper} sx={{ p: 3, mt: 0 }}>
        <ReportDateRangeFilter onRangeChange={onDateRangeChange} />
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 2 }}
          flexWrap="wrap"
          gap={1}
        >
          <Typography variant="h6">Orders</Typography>
          <LoadingButton
            type="button"
            variant="outlined"
            color="primary"
            endIcon={<DownloadIcon />}
            loading={exportCsvLoading}
            loadingPosition="end"
            onClick={exportCsv}
          >
            Export all CSV
          </LoadingButton>
        </Stack>
        <Table
          wrapperHeight={700}
          fetchItems={fetchOrdersReport}
          fetchItemsCustomParams={dateRange ?? {}}
          rows={ordersReportItems}
          columns={columns.current}
          getRowId={ordersReportRowId}
          loading={fetchOrdersReportStatus === fetchStatuses.pending}
          rowCount={totalOrdersReportItems}
          initialSortModel={[{ field: 'order_created_date', sort: 'desc' }]}
        />
      </Grid>
    </>
  );
}
